import { useEffect, useState } from "react";
import { BosDurum, Buton, Iskelet, Rozet } from "@koclukpanel/ui";
import { ApiHatasi } from "./api";
import { liderlikGetir } from "./api-oyun2";
import { hataMetni } from "./hata-metni";
import type { Liderlik as LiderlikVerisi } from "@koclukpanel/contracts";

/**
 * LİDERLİK — F-GAM-17, oyunlaştırma dalga 2 / sürüm 7b (S1: geçen haftanın KESİN tablosu, Pazartesi yenilenir).
 *
 * Ekran şunları HESAPLAMAZ: sıra (eşit puan aynı numarayı sunucudan alır), kısaltılmış ad, hafta kesinliği.
 * İstemciye tam ad, kimlik ya da fotoğraf gelmez; bu ekran da onları hiçbir yerde taşımaz.
 *
 * Dil bilerek sakin: "geride kaldın" / "seni geçti" gibi rekabet baskısı yok. Çark, davet ve elle verilen
 * puanlar tabloya girmez; ekran bunu tek cümleyle söyler, sayı hesabı yapmaz.
 */

/** `YYYY-AA-GG` → "21.09". UTC aritmetiği: yerel saat dilimi günü kaydıramaz. */
function gunAy(gun: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(gun);
  return m ? `${m[3]}.${m[2]}` : null;
}

/** Pazartesi'ye 6 gün ekler (Pazar). Geçersiz tarihte `null`: uydurma aralık yazmaktan iyidir. */
function pazarGunu(pazartesi: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(pazartesi);
  if (!m) return null;
  const [y, a, g] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const taban = new Date(Date.UTC(y, a - 1, g));
  if (taban.getUTCFullYear() !== y || taban.getUTCMonth() !== a - 1 || taban.getUTCDate() !== g) {
    return null;
  }
  return new Date(taban.getTime() + 6 * 86_400_000).toISOString().slice(0, 10);
}

/** "21.09–27.09 haftası" ya da çözülemezse `null`. */
export function haftaAraligi(hafta: string): string | null {
  const bas = gunAy(hafta);
  const sonGun = pazarGunu(hafta);
  const son = sonGun ? gunAy(sonGun) : null;
  return bas && son ? `${bas}–${son} haftası` : null;
}

export function Liderlik() {
  const [veri, setVeri] = useState<LiderlikVerisi | null>(null);
  const [kapali, setKapali] = useState(false);
  const [hata, setHata] = useState<string | null>(null);
  const [yenile, setYenile] = useState(0);

  useEffect(() => {
    let iptal = false;
    void (async () => {
      try {
        const v = await liderlikGetir();
        if (!iptal) {
          setHata(null);
          setVeri(v);
        }
      } catch (e) {
        if (iptal) return;
        if (e instanceof ApiHatasi && e.durum === 404) setKapali(true);
        else setHata(hataMetni(e, "Liderlik alınamadı."));
      }
    })();
    return () => {
      iptal = true;
    };
  }, [yenile]);

  if (kapali) return <p className="text-notr-600 text-sm">Liderlik şu an kapalı.</p>;
  if (hata) {
    return (
      <div className="flex flex-col items-start gap-2">
        <p role="alert" className="rounded-alan bg-hata-50 text-hata-700 w-full px-3 py-2 text-sm">
          {hata}
        </p>
        <Buton
          gorunum="ikincil"
          onClick={() => {
            setHata(null);
            setVeri(null);
            setYenile((n) => n + 1);
          }}
        >
          Tekrar dene
        </Buton>
      </div>
    );
  }
  if (veri === null) return <Iskelet satir={5} />;

  const aralik = haftaAraligi(veri.hafta);

  return (
    <section aria-labelledby="liderlik-baslik" className="flex flex-col gap-3">
      <header className="flex flex-col gap-1">
        <h3 id="liderlik-baslik" className="text-notr-900 text-base font-semibold">
          Haftalık liderlik
        </h3>
        {aralik && <p className="text-notr-800 text-sm font-medium">{aralik}</p>}
        <p className="text-notr-600 text-sm">Aynı sınıftaki öğrencilerle.</p>
        <p className="text-notr-600 text-sm">Yalnız görev ve deneme puanları sayılır.</p>
        <p className="text-notr-600 text-sm">
          Tablo her Pazartesi yenilenir.
          {!veri.kesin && " Çarşamba günü kesinleşir."}
        </p>
      </header>

      {veri.satirlar.length === 0 ? (
        <BosDurum baslik="Bu hafta için liste henüz yok." />
      ) : (
        <div className="border-notr-200 bg-notr-0 rounded-kart overflow-hidden border">
          <table className="w-full border-collapse text-sm">
            <caption className="sr-only">
              {aralik ? `Haftalık liderlik, ${aralik}` : "Haftalık liderlik"}
            </caption>
            <thead>
              <tr className="border-notr-200 text-notr-600 border-b text-left">
                <th scope="col" className="w-14 px-3 py-2 font-medium">
                  Sıra
                </th>
                <th scope="col" className="px-3 py-2 font-medium">
                  Öğrenci
                </th>
                <th scope="col" className="px-3 py-2 text-right font-medium">
                  Puan
                </th>
              </tr>
            </thead>
            <tbody>
              {veri.satirlar.map((s, i) => (
                <tr
                  key={`${s.sira}-${i}`}
                  aria-current={s.benMi ? "true" : undefined}
                  data-ben={s.benMi ? "evet" : undefined}
                  className={
                    s.benMi
                      ? "bg-marka-50 border-notr-100 border-b font-semibold"
                      : "border-notr-100 border-b"
                  }
                >
                  {/* Sol çizgi renk dışı ikinci işaret; metin etiketi ("Sen") yine de var. */}
                  <td
                    className={`text-notr-900 border-l-4 px-3 py-3 tabular-nums ${s.benMi ? "border-l-marka-600" : "border-l-transparent"}`}
                  >
                    {s.sira}
                  </td>
                  <td className="text-notr-900 px-3 py-3">
                    <span className="inline-flex flex-wrap items-center gap-2">
                      {s.ad}
                      {/* Renk tek işaret değil: vurgulu satırın yanında yazılı etiket de var. */}
                      {s.benMi && <Rozet ton="bilgi">Sen</Rozet>}
                    </span>
                  </td>
                  <td className="text-notr-900 px-3 py-3 text-right tabular-nums">{s.puan}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {veri.ben && (
        <p className="text-notr-900 text-sm font-medium">
          Senin sıran: <span className="tabular-nums">{veri.ben.sira}</span> ·{" "}
          <span className="tabular-nums">{veri.ben.puan}</span> puan
        </p>
      )}
    </section>
  );
}
